import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Target, TrendingDown, TrendingUp, AlertTriangle, CheckCircle2, ArrowUp, ArrowDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

export const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState('france');
  const [vision, setVision] = useState('ytd');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    fetchData();
    return () => setMounted(false);
  }, []);

  const fetchData = async () => {
    try {
      const response = await dashboardAPI.getOverview();
      setData(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodeChange = (newPeriode) => {
    if (mounted) {
      setPeriode(newPeriode);
    }
  };

  const handleVisionChange = (newVision) => {
    if (mounted) {
      setVision(newVision);
    }
  };

  // Données intelligentes par périmètre
  const getDataByPerimetre = () => {
    const datasets = {
      tunisie: {
        currency: '€',
        margeTech: '+6,5%',
        margeTechTrend: '-1,2 pts vs N-1',
        ratioCombine: '93,5%',
        ratioTrend: '-2,3 pts (Amélioration)',
        resultatTech: '+8,3M',
        resultatTrend: '+12,4% vs Budget',
        volumePrimes: '128,4M',
        volumeTrend: '+18,2% (Organique)',
        businessLines: [
          { name: 'Automobile', status: 'RENTABLE', statusColor: 'text-cyan-600 bg-cyan-50', margeTech: '+4,2%', ratioCombine: '95,8%', contribution: '+2,8M €', performance: 95 },
          { name: 'Santé (Groupe)', status: 'DESTRUCTION DE VALEUR', statusColor: 'text-red-600 bg-red-50', margeTech: '-3,8%', ratioCombine: '103,8%', contribution: '-1,4M €', performance: 35, alert: true },
          { name: 'MRH & Risques Divers', status: 'EXCELLENCE TECHNIQUE', statusColor: 'text-teal-600 bg-teal-50', margeTech: '+8,1%', ratioCombine: '91,9%', contribution: '+3,2M €', performance: 98 },
          { name: 'Vie & Capitalisation', status: 'RENTABLE', statusColor: 'text-cyan-600 bg-cyan-50', margeTech: '+5,6%', ratioCombine: '94,4%', contribution: '+3,7M €', performance: 94 }
        ],
        vigilance: [
          { compte: 'PharmaPlus SA', activite: 'Pharmaceutique', primes: '1,2M €', margeTech: '-18%', score: 82, classification: 'CRITIQUE - ACTION REQUISE', classColor: 'bg-red-100 text-red-800' },
          { compte: 'TechCorp Industries', activite: 'Technologie', primes: '890K €', margeTech: '-9%', score: 71, classification: 'SURVEILLANCE RENFORCÉE', classColor: 'bg-orange-100 text-orange-800' },
          { compte: 'MediGroup France', activite: 'Santé', primes: '1,5M €', margeTech: '-12%', score: 76, classification: 'SURVEILLANCE RENFORCÉE', classColor: 'bg-orange-100 text-orange-800' }
        ]
      },
      france: {
        currency: '€',
        margeTech: '+7,2%',
        margeTechTrend: '+0,8 pts vs N-1',
        ratioCombine: '92,1%',
        ratioTrend: '-3,1 pts (Amélioration)',
        resultatTech: '+42,6M',
        resultatTrend: '+15,8% vs Budget',
        volumePrimes: '592,3M',
        volumeTrend: '+22,4% (Organique)',
        businessLines: [
          { name: 'Automobile', status: 'EXCELLENCE TECHNIQUE', statusColor: 'text-teal-600 bg-teal-50', margeTech: '+9,1%', ratioCombine: '90,9%', contribution: '+18,5M €', performance: 97 },
          { name: 'Santé Collective', status: 'RENTABLE', statusColor: 'text-cyan-600 bg-cyan-50', margeTech: '+5,3%', ratioCombine: '94,7%', contribution: '+12,2M €', performance: 91 },
          { name: 'Habitation & RC', status: 'RENTABLE', statusColor: 'text-cyan-600 bg-cyan-50', margeTech: '+6,8%', ratioCombine: '93,2%', contribution: '+8,4M €', performance: 93 },
          { name: 'Vie & Épargne', status: 'EXCELLENCE TECHNIQUE', statusColor: 'text-teal-600 bg-teal-50', margeTech: '+10,2%', ratioCombine: '89,8%', contribution: '+3,5M €', performance: 99 }
        ],
        vigilance: [
          { compte: 'Groupe Carrefour Assurances', activite: 'Grande Distribution', primes: '8,4M €', margeTech: '-6%', score: 68, classification: 'SURVEILLANCE RENFORCÉE', classColor: 'bg-orange-100 text-orange-800' },
          { compte: 'BNP Paribas Cardif', activite: 'Bancassurance', primes: '15,2M €', margeTech: '+2%', score: 45, classification: 'SOUS SURVEILLANCE', classColor: 'bg-yellow-100 text-yellow-800' },
          { compte: 'Malakoff Humanis', activite: 'Prévoyance', primes: '12,8M €', margeTech: '+4%', score: 38, classification: 'CONFORME', classColor: 'bg-green-100 text-green-800' }
        ]
      },
      global: {
        currency: '€',
        margeTech: '+6,9%',
        margeTechTrend: '+0,2 pts vs N-1',
        ratioCombine: '92,8%',
        ratioTrend: '-2,7 pts (Amélioration)',
        resultatTech: '+51,2M',
        resultatTrend: '+14,3% vs Budget',
        volumePrimes: '724,8M',
        volumeTrend: '+20,6% (Organique)',
        businessLines: [
          { name: 'Automobile (Consolidé)', status: 'RENTABLE', statusColor: 'text-cyan-600 bg-cyan-50', margeTech: '+7,1%', ratioCombine: '92,9%', contribution: '+21,8M €', performance: 96 },
          { name: 'Santé (Consolidé)', status: 'RENTABLE', statusColor: 'text-cyan-600 bg-cyan-50', margeTech: '+3,8%', ratioCombine: '96,2%', contribution: '+10,6M €', performance: 88 },
          { name: 'MRH & Habitation', status: 'EXCELLENCE TECHNIQUE', statusColor: 'text-teal-600 bg-teal-50', margeTech: '+8,9%', ratioCombine: '91,1%', contribution: '+11,9M €', performance: 97 },
          { name: 'Vie & Capitalisation', status: 'EXCELLENCE TECHNIQUE', statusColor: 'text-teal-600 bg-teal-50', margeTech: '+9,4%', ratioCombine: '90,6%', contribution: '+6,9M €', performance: 98 }
        ],
        vigilance: [
          { compte: 'Consolidated Risk Pool A', activite: 'Multi-secteurs', primes: '22,6M €', margeTech: '-4%', score: 65, classification: 'SURVEILLANCE RENFORCÉE', classColor: 'bg-orange-100 text-orange-800' },
          { compte: 'European Healthcare Alliance', activite: 'Santé', primes: '18,9M €', margeTech: '+1%', score: 52, classification: 'SOUS SURVEILLANCE', classColor: 'bg-yellow-100 text-yellow-800' },
          { compte: 'Global Insurance Partners', activite: 'Réassurance', primes: '31,4M €', margeTech: '+6%', score: 35, classification: 'CONFORME', classColor: 'bg-green-100 text-green-800' }
        ]
      }
    };
    return datasets[periode] || datasets.tunisie;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  const currentData = getDataByPerimetre();

  // KPIs principaux dynamiques
  // KPIs principaux dynamiques
  const kpis = [
    {
      title: 'MARGE TECHNIQUE NETTE',
      value: currentData.margeTech,
      subtitle: 'Création de Valeur Structurelle',
      trend: currentData.margeTechTrend,
      icon: Target,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50 dark:bg-cyan-950',
      trendDown: currentData.margeTechTrend.includes('-')
    },
    {
      title: 'RATIO COMBINÉ (NET)',
      value: currentData.ratioCombine,
      subtitle: 'Sous Seuil de Rentabilité',
      trend: currentData.ratioTrend,
      icon: TrendingUp,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50 dark:bg-cyan-950',
      trendDown: true
    },
    {
      title: 'RÉSULTAT TECHNIQUE NORMALISÉ',
      value: currentData.resultatTech,
      unit: currentData.currency,
      subtitle: 'Aligné Appétit Risque',
      trend: currentData.resultatTrend,
      icon: TrendingUp,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50 dark:bg-cyan-950',
      trendUp: true
    },
    {
      title: 'VOLUME PRIMES ACQUISES',
      value: currentData.volumePrimes,
      unit: currentData.currency,
      subtitle: 'Trajectoire Croissance Contrôlée',
      trend: currentData.volumeTrend,
      icon: TrendingUp,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50 dark:bg-cyan-950',
      trendUp: true
    }
  ];

  const businessLines = currentData.businessLines;

  // Signaux de pilotage
  const signaux = [
    {
      title: 'Dégradation Structurelle Marge Santé',
      type: 'critique',
      diagnostic: 'Dérive des coûts moyens (+12%) non compensée par indexation. Concentration du risque sur segment Corporate (48% charge sinistre).',
      action: 'Redressement tarifaire sélectif & Révision plafonds garanties',
      exposition: '4,6M TND (Risque Technique)'
    },
    {
      title: 'Dérive Fréquence Auto (Flottes)',
      type: 'surveillance',
      diagnostic: 'Zone Grand Tunis : Sévérité stable mais fréquence en hausse (+23%). Suspicion fraude organisée ou dérive comportementale.',
      action: 'Audit portefeuille & Durcissement critères souscription',
      impact: 'Impact Marge : -3,6M TND (Projection annuelle)'
    }
  ];

  // Vigilance portefeuille (comptes majeurs)
  const vigilance = currentData.vigilance;

  return (
    <div className="p-8 space-y-8" data-testid="dashboard-page">
      {/* Header avec filtres */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#1E3A5F' }}>
              Pilotage Stratégique & Technique
            </h1>
            <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-200 px-2 py-0.5 text-xs">
              🤖 IA temps réel
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1.5">Supervision de la rentabilité technique - Données adaptées par périmètre</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <select 
            value={periode} 
            onChange={(e) => handlePeriodeChange(e.target.value)}
            className="w-[180px] h-9 text-sm px-3 rounded-md border border-input bg-background"
          >
            <option value="france">Périmètre : France</option>
            <option value="tunisie">Périmètre : Tunisie</option>
            <option value="global">Périmètre : Global</option>
          </select>
          <select 
            value={vision} 
            onChange={(e) => handleVisionChange(e.target.value)}
            className="w-[180px] h-9 text-sm px-3 rounded-md border border-input bg-background"
          >
            <option value="ytd">Vision : YTD</option>
            <option value="qtd">Vision : QTD</option>
            <option value="mtd">Vision : MTD</option>
          </select>
        </div>
      </div>

      {/* KPIs principaux */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((kpi, index) => {
          const Icon = kpi.icon;
          return (
            <Card key={index} className="stat-card" data-testid={`kpi-card-${index}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-12 h-12 rounded-xl ${kpi.bgColor} flex items-center justify-center`}>
                    <Icon className={kpi.color} size={24} />
                  </div>
                  <div className="text-right">
                    {kpi.trendDown && <TrendingDown className="text-green-600" size={18} />}
                    {kpi.trendUp && <TrendingUp className="text-green-600" size={18} />}
                  </div>
                </div>
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">{kpi.title}</p>
                <div className="flex items-baseline gap-1.5">
                  <p className="text-2xl font-bold data-cell" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {kpi.value}
                  </p>
                  {kpi.unit && <span className="text-sm text-muted-foreground">{kpi.unit}</span>}
                </div>
                <p className="text-xs text-cyan-600 font-medium mt-1.5">{kpi.subtitle}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{kpi.trend}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Performance par ligne de business */}
      <Card data-testid="business-lines-performance">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Performance Technique par Ligne de Business</CardTitle>
              <CardDescription className="text-sm">Décomposition de la rentabilité et contribution au résultat</CardDescription>
            </div>
            <Badge variant="outline" className="text-xs">Audit T4</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {businessLines.map((line, index) => (
            <div key={index} className={`p-4 rounded-lg border-2 ${line.alert ? 'border-red-300 bg-red-50/50' : 'border-border bg-card'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`px-2 py-0.5 rounded text-xs font-semibold ${line.statusColor}`}>
                    {line.status}
                  </div>
                  <h3 className="text-lg font-bold">{line.name}</h3>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-0.5">MARGE TECH.</p>
                    <p className={`font-bold text-sm ${line.margeTech.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                      {line.margeTech}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-0.5">RATIO COMBINÉ</p>
                    <p className="font-bold text-sm">{line.ratioCombine}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-0.5">CONTRIBUTION</p>
                    <p className={`font-bold text-sm ${line.contribution.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                      {line.contribution}
                    </p>
                  </div>
                </div>
              </div>
              {/* Progress bar */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Performance</span>
                  <span className="text-muted-foreground">Seuil rentabilité (100%)</span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${line.alert ? 'bg-red-500' : line.status === 'EXCELLENCE TECHNIQUE' ? 'bg-teal-500' : 'bg-cyan-500'}`}
                    style={{ width: `${line.performance}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Signaux de pilotage et Vigilance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Signaux de pilotage */}
        <Card data-testid="signaux-pilotage">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-orange-600" size={24} />
              <CardTitle>Signaux de Pilotage</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {signaux.map((signal, index) => (
              <div key={index} className={`p-4 rounded-lg border-l-4 ${signal.type === 'critique' ? 'border-red-500 bg-red-50/50' : 'border-orange-500 bg-orange-50/50'}`}>
                <h4 className="font-bold text-sm mb-2">{signal.title}</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  <span className="font-semibold">Diagnostic :</span> {signal.diagnostic}
                </p>
                <div className="flex items-start gap-2 mb-2">
                  <ArrowUp className="text-primary flex-shrink-0 mt-0.5" size={14} />
                  <p className="text-xs font-medium">{signal.action}</p>
                </div>
                <Badge variant="outline" className="text-xs">
                  ⚠️ {signal.exposition || signal.impact}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Vigilance portefeuille */}
        <Card data-testid="vigilance-portefeuille">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={24} />
              <div>
                <CardTitle>Vigilance Portefeuille & Risques de Pointe</CardTitle>
                <CardDescription className="text-xs mt-1">
                  Surveillance des comptes majeurs présentant une dérive technique ou un risque de volatilité.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground">COMPTE / SEGMENT</th>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground">ACTIVITÉ</th>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground">PRIMES ACQUISES</th>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground">MARGE TECHNIQUE</th>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground">SCORE D'EXPOSITION</th>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground">CLASSIFICATION RISQUE</th>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground">LEVIER D'ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {vigilance.map((item, index) => (
                    <tr key={index} className="border-b hover:bg-secondary/30">
                      <td className="p-3 font-semibold">{item.compte}</td>
                      <td className="p-3 text-muted-foreground">{item.activite}</td>
                      <td className="p-3 font-mono">{item.primes}</td>
                      <td className="p-3 font-bold text-red-600">{item.margeTech}</td>
                      <td className="p-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-red-500 h-2 rounded-full" style={{ width: `${item.score}%` }} />
                        </div>
                        <span className="text-xs font-mono">{item.score}</span>
                      </td>
                      <td className="p-3">
                        <Badge className={item.classColor + ' text-xs'}>
                          {item.classification}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <Button variant="outline" size="sm" className="text-xs">
                          📋 Recommandation
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Avertissement de gouvernance */}
      <Card className="bg-blue-50/50 border-blue-200">
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold">Avertissement de gouvernance :</span> Les indicateurs présentés (Moteur d'Aide à la Décision) sont générés sur la base des modèles actuariels calibrés au 31/12/N-1. Ils constituent une aide au pilotage et ne se substituent pas à la validation par les comités techniques et de souscription. Les projections de marge incluent une charge de sinistre ultime (IBNR) estimée.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
